import React from 'react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { 
  Calculator, ArrowRight, Check, FileText, 
  Clock, Shield, Gift, ChevronRight, Phone
} from 'lucide-react';

export const CreditImpotPage = () => {
  return (
    <div className="min-h-screen bg-white font-['Poppins']">
      {/* Hero Section - Design épuré sans formulaire */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#153254] to-[#0A2320]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#D4AF37] text-white px-6 py-2 rounded-full text-sm font-bold mb-8 shadow-lg">
              <span className="text-xl">★</span>
              50% de crédit d'impôt immédiat
              <span className="text-xl">★</span>
            </div>
            
            {/* Titre principal */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Le crédit d'impôt pour les<br />
              <span className="text-[#D4AF37]">services à la personne</span>
            </h1>
            
            {/* Texte accrocheur */}
            <div className="mb-10">
              <p className="text-2xl md:text-3xl text-white/90 font-light mb-2">
                C'est <span className="line-through text-white/50">une fois par an</span>
              </p>
              <p className="text-3xl md:text-4xl font-bold text-[#D4AF37]">
                immédiat !
              </p>
            </div>
            
            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis-premium">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-bold text-lg px-10 py-6 rounded-xl shadow-lg">
                  <Calculator className="mr-2 h-5 w-5" />
                  Demander un devis gratuit
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#153254] font-semibold text-lg px-10 py-6 rounded-xl"
                onClick={() => window.location.href = 'tel:0981543842'}
              >
                <Phone className="mr-2 h-5 w-5" />
                09 81 54 38 42
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section partenaires officiels - SAP et URSSAF */}
      <section className="py-10 bg-[#F8FAF9]">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-gray-500 mb-6 uppercase tracking-wider">Nos partenaires officiels</p>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-20">
            {/* Services à la Personne (SAP) - Vrai logo */}
            <div className="opacity-90 hover:opacity-100 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_concierge-website/artifacts/fyr6pcjx_Traceur_SAP_buro_rvb_png.png"
                alt="Services à la Personne"
                className="h-14 md:h-18 w-auto object-contain"
              />
            </div>

            {/* Urssaf - Vrai logo - Plus grand */}
            <div className="opacity-90 hover:opacity-100 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_concierge-website/artifacts/u7emij6n_logo-urssaf-2.png"
                alt="URSSAF"
                className="h-14 md:h-18 w-auto object-contain"
              />
            </div>
            
            {/* CESU - Logo */}
            <div className="opacity-90 hover:opacity-100 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_2513ce8b-8bd6-4c11-809d-00965abc9d4a/artifacts/5w7l650a_Logo_cncesu.png"
                alt="CESU - Chèque Emploi Service Universel"
                className="h-14 md:h-18 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Infographie - Comment ça marche */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#153254] mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            En 3 étapes simples, bénéficiez immédiatement de votre crédit d'impôt
          </p>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {/* Étape 1 */}
              <div className="relative">
                <div className="bg-[#F8FAF9] rounded-2xl p-8 shadow-md border border-gray-100 h-full">
                  <div className="absolute -top-5 left-8 w-10 h-10 bg-[#0A2320] rounded-full flex items-center justify-center text-white font-bold shadow-md">
                    1
                  </div>
                  <div className="mt-4">
                    <div className="w-14 h-14 bg-[#0A2320]/10 rounded-xl flex items-center justify-center mb-4">
                      <FileText className="h-7 w-7 text-[#0A2320]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#153254] mb-3">Demandez un devis</h3>
                    <p className="text-gray-600">
                      Sélectionnez vos services et recevez un devis gratuit et personnalisé en quelques minutes.
                    </p>
                  </div>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ChevronRight className="h-8 w-8 text-[#0A2320]" />
                </div>
              </div>
              
              {/* Étape 2 */}
              <div className="relative">
                <div className="bg-[#F8FAF9] rounded-2xl p-8 shadow-md border border-gray-100 h-full">
                  <div className="absolute -top-5 left-8 w-10 h-10 bg-[#0A2320] rounded-full flex items-center justify-center text-white font-bold shadow-md">
                    2
                  </div>
                  <div className="mt-4">
                    <div className="w-14 h-14 bg-[#0A2320]/10 rounded-xl flex items-center justify-center mb-4">
                      <Shield className="h-7 w-7 text-[#0A2320]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#153254] mb-3">Activez l'avance</h3>
                    <p className="text-gray-600">
                      Nous vous aidons à activer l'avance immédiate sur le site de l'URSSAF. C'est gratuit et rapide.
                    </p>
                  </div>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ChevronRight className="h-8 w-8 text-[#0A2320]" />
                </div>
              </div>
              
              {/* Étape 3 */}
              <div className="relative">
                <div className="bg-[#F8FAF9] rounded-2xl p-8 shadow-md border border-gray-100 h-full">
                  <div className="absolute -top-5 left-8 w-10 h-10 bg-[#0A2320] rounded-full flex items-center justify-center text-white font-bold shadow-md">
                    3
                  </div>
                  <div className="mt-4">
                    <div className="w-14 h-14 bg-[#0A2320]/10 rounded-xl flex items-center justify-center mb-4">
                      <Gift className="h-7 w-7 text-[#0A2320]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#153254] mb-3">Payez 50% moins</h3>
                    <p className="text-gray-600">
                      Vous ne payez que la moitié ! Le crédit d'impôt est déduit automatiquement de chaque facture.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Calculateur visuel */}
      <section className="py-20 bg-[#F8FAF9]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#153254] mb-6">
                  Voyez la différence
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  Avec l'avance immédiate de crédit d'impôt, votre budget va deux fois plus loin. 
                  Voici un exemple concret pour 4 heures de ménage par mois.
                </p>
                
                <div className="space-y-4">
                  {['Ménage régulier', 'Jardinage et entretien', 'Petits travaux de bricolage', 'Aide administrative'].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-[#0A2320] rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-[#0A2320] to-[#153254] rounded-3xl p-8 text-[#F8FAF9] shadow-xl">
                <h3 className="text-2xl font-bold mb-6 text-[#F8FAF9]">Exemple mensuel</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-[#F8FAF9]/20">
                    <span className="text-[#F8FAF9]/80">4h de ménage × 25€/h</span>
                    <span className="text-2xl font-bold">100 €</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-4 border-b border-[#F8FAF9]/20">
                    <span className="text-[#D4AF37]">Crédit d'impôt immédiat</span>
                    <span className="text-2xl font-bold text-[#D4AF37]">- 50 €</span>
                  </div>
                  
                  <div className="bg-[#F8FAF9]/10 rounded-xl p-6 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Vous payez</span>
                      <span className="text-4xl font-bold">50 €</span>
                    </div>
                    <p className="text-[#F8FAF9]/70 text-sm mt-2">soit 12,50€ de l'heure !</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#153254] mb-12">
            Questions fréquentes
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "Qui peut bénéficier du crédit d'impôt ?",
                a: "Tous les résidents fiscaux français, qu'ils soient imposables ou non. Même si vous ne payez pas d'impôts, vous recevez un virement de l'État !"
              },
              {
                q: "Qu'est-ce que l'avance immédiate ?",
                a: "Au lieu d'attendre votre déclaration d'impôts pour récupérer 50%, vous ne payez directement que la moitié. Le crédit est appliqué automatiquement à chaque facture."
              },
              {
                q: "Quels services sont éligibles ?",
                a: "Le ménage, le jardinage (jusqu'à 5000€/an), le bricolage (jusqu'à 500€/an), l'aide administrative, et bien d'autres services à la personne."
              },
              {
                q: "Comment activer l'avance immédiate ?",
                a: "Nous vous accompagnons ! Il suffit de créer un compte sur le site de l'URSSAF (service gratuit). Nous pouvons vous guider pas à pas."
              },
              {
                q: "Existe-t-il un plafond ?",
                a: "Le crédit d'impôt est plafonné à 12 000€ par an (soit 6 000€ de réduction maximum). Ce plafond est majoré selon votre situation familiale."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-[#F8FAF9] rounded-2xl shadow-sm group">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="font-bold text-[#153254] text-lg pr-8">{faq.q}</span>
                  <ChevronRight className="h-5 w-5 text-[#0A2320] transform transition-transform group-open:rotate-90 flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-[#0A2320] to-[#153254]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à économiser 50% ?
          </h2>
          <p className="text-lg text-white mb-10 max-w-2xl mx-auto">
            Rejoignez les milliers de foyers qui profitent déjà de l'avance immédiate de crédit d'impôt
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis-premium">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-semibold text-lg px-10 py-6 shadow-lg rounded-xl">
                <Calculator className="mr-2 h-5 w-5" />
                Demander mon devis gratuit
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#153254] font-semibold text-lg px-10 py-6 rounded-xl"
              onClick={() => window.location.href = 'tel:0981543842'}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 81 54 38 42
            </Button>
          </div>
          
          <p className="text-white/60 text-sm mt-6">
            Sans engagement • Réponse sous 24h
          </p>
        </div>
      </section>
    </div>
  );
};
